import { storageKeys, timing } from "../config";
/**
 * The operator's wallets, over raw EIP-1193 and EIP-6963, plus WalletConnect:
 * no wallet framework. The portal needs to connect, sign the sign-in message
 * (`personal_sign`), switch the network and send the owner calls a page
 * prepared (`eth_sendTransaction`), and, for the desk's transfer to its
 * treasury, read the wallet's own balance and the transfer's receipt.
 *
 * Which wallet: the one the operator PICKED from the list (the wallets the
 * browser announces, EIP-6963 "Multi Injected Provider Discovery", keyed by
 * rdns, and WalletConnect when the boot descriptor publishes a project id),
 * and only that one. The pick is remembered (`aile.maker.wallet.rdns`), and
 * every call (accounts, network, signature, transaction, events) goes to it.
 * There is no fallback: with no pick, a picked extension that is no longer in
 * the browser, or a picked WalletConnect with no live session (none yet,
 * ended on the phone, expired), nothing is asked of any wallet and the
 * screens show the list again. Several extensions fight over the injected
 * global, so whatever won that race is never used; one installed wallet is
 * still a pick, not a default.
 *
 * WalletConnect (`./walletConnect`) is one lazily-imported EIP-1193 provider
 * behind this same interface: pairing is `enable()` (the QR sheet), every
 * request is a relay round-trip to a phone with a deadline, and a change of
 * wallet, Cancel or sign-out hangs the pairing up so the phone stops showing
 * it.
 *
 * One request at a time per wallet. A request that opens a prompt
 * (connect, sign, switch, send) is refused here, before the wallet sees it,
 * while another prompt of ours is open in that wallet: a second
 * `eth_requestAccounts` only earns -32002 and looks like a dead button. Reads
 * (`eth_accounts`, `eth_chainId`) wait for an open prompt, and identical
 * reads in flight share one answer, so an effect cannot fire a parallel
 * request either. A prompt never waits: an extension opens its popup only
 * while the click's user gesture is on the stack.
 *
 * Every provider failure becomes a `WalletError` carrying `{ code, message }`;
 * only `isReportableWalletError` failures go to the error reporter.
 */

import { toHex } from "viem";
import { chainLabel, toChainIdHex } from "./chain";
import {
  clearWalletConnectUnclean,
  closeWalletConnectModal,
  disconnectWalletConnect,
  enableWalletConnect,
  ensureWalletConnectChain,
  getWalletConnectProvider,
  hangUpOrphan,
  hasWalletConnectSession,
  initWalletConnect,
  isWalletConnectExpiry,
  isWalletConnectMethodRefusal,
  isWalletConnectRejection,
  isWalletConnectReset,
  onWalletConnectDisconnected,
  orphanHangUpsSettled,
  resetWalletConnect,
  sessionGrantsMethods,
  sessionGrantsSigning,
  walletConnectApproves,
  walletConnectGeneration,
  walletConnectHoldOn,
  walletConnectHome,
  walletConnectNetworks,
  walletConnectTeardownUnclean,
  WalletConnectUnavailable,
  WALLETCONNECT_EXPIRED,
  WALLETCONNECT_ICON,
  WALLETCONNECT_NAME,
  WALLETCONNECT_RDNS,
  WALLETCONNECT_RECONNECT,
  WALLETCONNECT_TIMEOUT,
  WALLETCONNECT_UNAVAILABLE,
  WALLETCONNECT_USER_REJECTED,
  type WalletConnectProvider,
} from "./walletConnect";

/** Minimal EIP-1193 surface: everything this app touches. */
export interface Eip1193Provider {
  request(args: { method: string; params?: readonly unknown[] }): Promise<unknown>;
  on?(event: string, listener: (...args: never[]) => void): void;
  removeListener?(event: string, listener: (...args: never[]) => void): void;
}

/** `info` half of an `eip6963:announceProvider` event. */
export interface Eip6963ProviderInfo {
  uuid: string;
  /** What the wallet calls itself: "MetaMask", "Phantom". */
  name: string;
  /** Reverse-DNS id ("io.metamask"): the stable key a pick is remembered by. */
  rdns: string;
  /** A `data:` URI per EIP-6963; empty when the wallet sent none. */
  icon: string;
}

/** One announced wallet: its metadata plus the provider object to talk to. */
export interface Eip6963ProviderDetail {
  info: Eip6963ProviderInfo;
  provider: Eip1193Provider;
}

/** EIP-1193 "user rejected the request". */
export const USER_REJECTED = 4001;
/** EIP-1193 "request already pending": a prompt is open in the wallet. */
export const REQUEST_PENDING = -32002;
/** EIP-3326 "chain not added to the wallet yet". */
export const CHAIN_NOT_ADDED = 4902;
/** Synthetic: no wallet announced itself in this browser. */
export const NO_WALLET = "NO_WALLET";
/** Synthetic: wallets are there, but none is picked (or the picked one is gone). */
export const NO_WALLET_CHOSEN = "NO_WALLET_CHOSEN";
/** Synthetic: the wallet answered with no account (locked, or this site not authorised). */
export const WALLET_LOCKED = "WALLET_LOCKED";
/** Synthetic: the picked wallet cannot show its account list here (WalletConnect, or none picked). */
export const NO_ACCOUNT_CHOOSER_CODE = "NO_ACCOUNT_CHOOSER";

/** localStorage key holding the rdns of the wallet the operator picked. */
export const SELECTED_RDNS_KEY = storageKeys.walletSelection;

/** WalletConnect's synthetic rdns and codes, re-exported so callers need one import. */
export {
  WALLETCONNECT_EXPIRED,
  WALLETCONNECT_HINT,
  WALLETCONNECT_ICON,
  WALLETCONNECT_NAME,
  WALLETCONNECT_RDNS,
  WALLETCONNECT_RECONNECT,
  WALLETCONNECT_TIMEOUT,
  WALLETCONNECT_UNAVAILABLE,
} from "./walletConnect";
/** The chain names the wallet's messages use; a page records the network it acts on. */
export { nameChain } from "./chain";

/** What -32002 tells the user, whether the wallet or this module refused the request. */
export const PENDING_MESSAGE = "Your wallet already has a request open: finish or close it there, then try again.";
/** No wallet announced itself (and WalletConnect is not offered). */
export const NO_WALLET_MESSAGE = "No wallet found in this browser. Install or turn on a wallet extension, then reload this page.";
/** The operator closed the QR sheet, or pressed Cancel: nothing was asked of any wallet. */
export const CONNECTION_CANCELLED_MESSAGE = "Connection cancelled. Nothing was shared.";

export class WalletError extends Error {
  readonly code: number | string;
  constructor(code: number | string, message: string) {
    super(message);
    this.name = "WalletError";
    this.code = code;
  }
}

/**
 * The operator backed out of a WalletConnect connection before any wallet was
 * asked anything: closed the QR sheet with its X, pressed Cancel, or picked
 * another wallet while the pairing was loading. Code 4001, so
 * `isUserRejected()` holds and it is never reported; its own class only so the
 * UI does not claim "you refused" when nothing was ever requested.
 */
export class ConnectionCancelled extends WalletError {
  constructor() {
    super(USER_REJECTED, CONNECTION_CANCELLED_MESSAGE);
    this.name = "ConnectionCancelled";
  }
}

export function isConnectionCancelled(err: unknown): boolean {
  return err instanceof ConnectionCancelled;
}

type Unsubscribe = () => void;

/** Every place a wallet puts its code: some nest the real one (`{ code: -32603, data: { code: 4001 } }`). */
function codesOf(err: unknown): unknown[] {
  if (err === null || typeof err !== "object") return [];
  const e = err as { code?: unknown; data?: { code?: unknown; originalError?: { code?: unknown } }; cause?: { code?: unknown } };
  return [e.code, e.data?.code, e.data?.originalError?.code, e.cause?.code].filter((c) => c !== undefined);
}

/** The user refused in the wallet (4001, a wallet's "ACTION_REJECTED", or WalletConnect's 5000 from a phone). */
export function isUserRejected(err: unknown): boolean {
  return codesOf(err).some(
    (c) => c === USER_REJECTED || c === "4001" || c === "ACTION_REJECTED" || c === WALLETCONNECT_USER_REJECTED || c === String(WALLETCONNECT_USER_REJECTED),
  );
}

/** A prompt is already open in the wallet (-32002): the user finishes it there. */
export function isRequestPending(err: unknown): boolean {
  return codesOf(err).some((c) => c === REQUEST_PENDING || c === "-32002");
}

export function toWalletError(err: unknown): WalletError {
  if (err instanceof WalletError) return err;
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "object" && err !== null && typeof (err as { message?: unknown }).message === "string"
        ? (err as { message: string }).message
        : String(err);
  const first = codesOf(err).find((c) => typeof c === "number" || typeof c === "string") as number | string | undefined;
  const code = isUserRejected(err) ? USER_REJECTED : isRequestPending(err) ? REQUEST_PENDING : (first ?? "UNKNOWN");
  return new WalletError(code, message);
}

/** Outcomes the user fixes in the browser or the wallet: never a fault. */
const USER_FIXABLE = new Set<number | string>([
  NO_WALLET,
  NO_WALLET_CHOSEN,
  WALLET_LOCKED,
  NO_ACCOUNT_CHOOSER_CODE,
  CHAIN_NOT_ADDED,
  // The phone was not at hand, the QR code was not scanned in time, or the
  // session lacks a network or a transaction grant: the operator acts.
  WALLETCONNECT_TIMEOUT,
  WALLETCONNECT_EXPIRED,
  WALLETCONNECT_RECONNECT,
]);

/** WalletConnect codes whose message is finished English, rendered as is. */
const WORDED = new Set<number | string>([WALLETCONNECT_UNAVAILABLE, WALLETCONNECT_TIMEOUT, WALLETCONNECT_EXPIRED, WALLETCONNECT_RECONNECT]);

/**
 * True for a failure worth the error reporter. Never the user's refusal
 * (4001), a request already open in the wallet (-32002), or a state the user
 * fixes (no wallet, none picked, locked, a network the wallet does not know,
 * a WalletConnect session to renew). A dead relay stays reportable: it is the
 * failure WalletConnect is most likely to hit in production.
 */
export function isReportableWalletError(err: unknown): boolean {
  if (isUserRejected(err) || isRequestPending(err)) return false;
  return !USER_FIXABLE.has(toWalletError(err).code);
}

/** A failure in words a person can act on. Never names a method or a library. */
export function explainWalletError(err: unknown): string {
  const e = toWalletError(err);
  if (isConnectionCancelled(e)) return CONNECTION_CANCELLED_MESSAGE;
  if (isUserRejected(e)) return "You refused the request in your wallet.";
  if (isRequestPending(e)) return PENDING_MESSAGE;
  if (WORDED.has(e.code)) return e.message;
  if (e.code === NO_WALLET) return NO_WALLET_MESSAGE;
  if (e.code === NO_WALLET_CHOSEN) return "Choose your wallet, then try again.";
  if (e.code === WALLET_LOCKED) return "Unlock your wallet and try again.";
  if (e.code === NO_ACCOUNT_CHOOSER_CODE) return "This wallet cannot show its accounts here: switch the account in the wallet, or use another wallet.";
  if (e.code === CHAIN_NOT_ADDED) return "Your wallet does not know this network yet: add it in the wallet, then try again.";
  return "Your wallet could not do this. Try again.";
}

// ---- EIP-6963 discovery ------------------------------------------------------------

const ANNOUNCE_EVENT = "eip6963:announceProvider";
const REQUEST_EVENT = "eip6963:requestProvider";

/** Announced wallets by rdns, in announcement order; the first announcement of an rdns wins. */
const announced = new Map<string, Eip6963ProviderDetail>();
const providerListeners = new Set<() => void>();

function parseAnnouncement(detail: unknown): Eip6963ProviderDetail | null {
  if (detail === null || typeof detail !== "object") return null;
  const { info, provider } = detail as { info?: unknown; provider?: unknown };
  if (info === null || typeof info !== "object") return null;
  if (provider === null || typeof provider !== "object") return null;
  if (typeof (provider as Eip1193Provider).request !== "function") return null;
  const { uuid, name, rdns, icon } = info as Record<string, unknown>;
  if (typeof rdns !== "string" || rdns === "") return null;
  if (typeof name !== "string" || name === "") return null;
  // WalletConnect's synthetic rdns is ours: no extension may take it over.
  if (rdns === WALLETCONNECT_RDNS) return null;
  return {
    info: { uuid: typeof uuid === "string" ? uuid : rdns, name, rdns, icon: typeof icon === "string" ? icon : "" },
    provider: provider as Eip1193Provider,
  };
}

function onAnnounce(event: Event): void {
  const parsed = parseAnnouncement((event as CustomEvent<unknown>).detail);
  // Wallets re-announce on every request event: one entry per rdns.
  if (parsed === null || announced.has(parsed.info.rdns)) return;
  announced.set(parsed.info.rdns, parsed);
  for (const listener of [...providerListeners]) listener();
}

// From module load: wallets announce once at page load, and an announcement
// that arrives before anyone listens is lost.
if (typeof window !== "undefined") {
  window.addEventListener(ANNOUNCE_EVENT, onAnnounce);
  window.dispatchEvent(new Event(REQUEST_EVENT));
}

/** The wallets announced so far, in announcement order. */
export function listProviders(): Eip6963ProviderDetail[] {
  return [...announced.values()];
}

/**
 * Asks every wallet to announce itself and resolves with the list. Most
 * answer inside `dispatchEvent`; a few from a task, so this yields once.
 */
export async function discoverProviders(): Promise<Eip6963ProviderDetail[]> {
  if (typeof window === "undefined") return [];
  window.dispatchEvent(new Event(REQUEST_EVENT));
  await new Promise<void>((resolve) => setTimeout(resolve, timing.wallet.discoveryWaitMs));
  return listProviders();
}

/** Fires when a new wallet announces itself. */
export function onProvidersChanged(listener: () => void): Unsubscribe {
  providerListeners.add(listener);
  return () => {
    providerListeners.delete(listener);
  };
}

/** The announced wallet for `rdns`, or null when it is not in this browser. */
export function findProvider(rdns: string): Eip6963ProviderDetail | null {
  return announced.get(rdns) ?? null;
}

/** True once any wallet announced itself. */
export function hasWallet(): boolean {
  return announced.size > 0;
}

// ---- WalletConnect's place among the wallets ----------------------------------------

/** WalletConnect as a row of the list and as the connected wallet's name and icon. */
export const WALLETCONNECT_INFO: Eip6963ProviderInfo = {
  uuid: WALLETCONNECT_RDNS,
  name: WALLETCONNECT_NAME,
  rdns: WALLETCONNECT_RDNS,
  icon: WALLETCONNECT_ICON,
};

/** The detail `selectedWallet()` answers for WalletConnect, kept while its provider lives. */
let walletConnectDetail: Eip6963ProviderDetail | null = null;

/** The WalletConnect provider when it holds a live session, else null. */
function liveWalletConnect(): WalletConnectProvider | null {
  const wc = getWalletConnectProvider();
  return wc !== null && hasWalletConnectSession(wc) ? wc : null;
}

/** The live WalletConnect provider when it is the operator's pick, else null. */
function pickedWalletConnect(): WalletConnectProvider | null {
  return getSelectedRdns() === WALLETCONNECT_RDNS ? liveWalletConnect() : null;
}

let walletConnectVersionNo = 0;
const walletConnectListeners = new Set<() => void>();

/** Announces that the WalletConnect provider was seated or dropped (the screens re-read the pick). */
function walletConnectChanged(): void {
  walletConnectVersionNo += 1;
  for (const listener of [...walletConnectListeners]) listener();
}

/**
 * Fires when a WalletConnect session becomes usable (paired, restored late at
 * boot) or stops being usable (hung up, ended on the phone, expired), so the
 * screens re-read the picked wallet and its account.
 */
export function onWalletConnectChanged(listener: () => void): Unsubscribe {
  walletConnectListeners.add(listener);
  return () => {
    walletConnectListeners.delete(listener);
  };
}

/** A number that changes with every `onWalletConnectChanged` (a React snapshot). */
export function walletConnectVersion(): number {
  return walletConnectVersionNo;
}

/** The phone ending the pairing (or its expiry) drops the provider: the screens show the list again. */
let endedOff: Unsubscribe | null = null;
function listenForEnd(): void {
  if (endedOff !== null) return;
  endedOff = onWalletConnectDisconnected(() => {
    void disconnectWalletConnect();
    walletConnectChanged();
  });
}

/** Drops the WalletConnect provider and hangs up on its pairing (never rejects, never waits). */
function tearDownWalletConnect(): void {
  const had = getWalletConnectProvider() !== null;
  void disconnectWalletConnect();
  if (had) walletConnectChanged();
}

// ---- the pick ------------------------------------------------------------------------

/**
 * In-memory mirror of the pick, authoritative for this page load once
 * anything was picked: `undefined` = ask storage; `null` = explicitly none.
 * A browser whose storage throws (privacy mode, a full quota) still keeps the
 * pick for the visit instead of losing it.
 */
let selectedMirror: string | null | undefined = undefined;
const selectionListeners = new Set<() => void>();

export function getSelectedRdns(): string | null {
  if (selectedMirror !== undefined) return selectedMirror;
  try {
    return localStorage.getItem(SELECTED_RDNS_KEY);
  } catch {
    return null;
  }
}

/**
 * Remembers the operator's pick (null forgets it). Every later call goes to
 * that wallet. Moving away from WalletConnect hangs its pairing up: the phone
 * must not keep a session for a wallet this browser no longer uses.
 */
export function selectProvider(rdns: string | null): void {
  const before = getSelectedRdns();
  selectedMirror = rdns;
  try {
    if (rdns === null) localStorage.removeItem(SELECTED_RDNS_KEY);
    else localStorage.setItem(SELECTED_RDNS_KEY, rdns);
  } catch {
    // Storage unavailable: the mirror holds the pick for this visit.
  }
  if (before === WALLETCONNECT_RDNS && rdns !== WALLETCONNECT_RDNS) tearDownWalletConnect();
  if (before !== rdns) for (const listener of [...selectionListeners]) listener();
}

export function onSelectionChanged(listener: () => void): Unsubscribe {
  selectionListeners.add(listener);
  return () => {
    selectionListeners.delete(listener);
  };
}

/**
 * The picked wallet when it can be used: an extension in this browser, or
 * WalletConnect with a live session. Else null (the screens show the list).
 */
export function selectedWallet(): Eip6963ProviderDetail | null {
  const rdns = getSelectedRdns();
  if (rdns === null) return null;
  if (rdns === WALLETCONNECT_RDNS) {
    const wc = liveWalletConnect();
    if (wc === null) return null;
    // One object per provider, as an announced wallet's detail is.
    if (walletConnectDetail?.provider !== wc) walletConnectDetail = { info: WALLETCONNECT_INFO, provider: wc };
    return walletConnectDetail;
  }
  return findProvider(rdns);
}

/**
 * The wallet a call goes to: `rdns` when given (a pick being made), else the
 * remembered pick. Never another wallet and never the injected global; a
 * picked WalletConnect with no live session is "none picked".
 */
function target(rdns?: string): Eip1193Provider {
  const wanted = rdns ?? getSelectedRdns();
  if (wanted === WALLETCONNECT_RDNS) {
    const wc = liveWalletConnect();
    if (wc !== null) return wc;
    throw new WalletError(NO_WALLET_CHOSEN, "WalletConnect is not connected");
  }
  const detail = wanted === null ? null : findProvider(wanted);
  if (detail !== null) return detail.provider;
  if (announced.size === 0) throw new WalletError(NO_WALLET, "No wallet in this browser");
  throw new WalletError(NO_WALLET_CHOSEN, "No wallet picked");
}

/** Forgets every announced wallet, the pick's mirror, the open requests and WalletConnect (tests). */
export function resetWalletDiscovery(): void {
  announced.clear();
  lanes.clear();
  selectedMirror = undefined;
  walletConnectInFlight = null;
  endedOff = null;
  resetWalletConnect();
}

// ---- one request at a time ------------------------------------------------------------

/** Methods that open a prompt in the wallet. */
const PROMPTS = new Set(["eth_requestAccounts", "wallet_requestPermissions", "personal_sign", "wallet_switchEthereumChain", "eth_sendTransaction"]);

interface Lane {
  /** The prompt of ours open in this wallet, if any. */
  prompt: Promise<unknown> | null;
  /** Reads in flight, by method and params. */
  reads: Map<string, Promise<unknown>>;
}

const lanes = new Map<Eip1193Provider, Lane>();

function laneOf(provider: Eip1193Provider): Lane {
  let lane = lanes.get(provider);
  if (lane === undefined) {
    lane = { prompt: null, reads: new Map() };
    lanes.set(provider, lane);
  }
  return lane;
}

/** The provider call itself: started synchronously, every failure a `WalletError`. */
async function ask<T>(provider: Eip1193Provider, method: string, params?: readonly unknown[]): Promise<T> {
  try {
    return (await provider.request(params === undefined ? { method } : { method, params })) as T;
  } catch (err) {
    throw toWalletError(err);
  }
}

function forget(pending: Promise<unknown>, clear: () => void): void {
  pending.then(clear, clear);
}

function withDeadline<T>(pending: Promise<T>, ms: number, onTimeout: () => WalletError): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(onTimeout()), ms);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** How a WalletConnect request differs: a step before it (the network check) and a deadline. */
interface Relay {
  before?: () => Promise<void>;
  deadlineMs: number;
}

/**
 * Sends one request through the wallet's lane (see the header). Not async on
 * purpose: a prompt reaches the provider (or, over WalletConnect, its network
 * check) before this returns, and the lane is taken at once. A WalletConnect
 * request's deadline frees the lane: "open your wallet app, then try again"
 * must be possible.
 */
function send<T>(provider: Eip1193Provider, method: string, params?: readonly unknown[], relay?: Relay): Promise<T> {
  const lane = laneOf(provider);
  const call = (): Promise<T> => {
    const pending = relay?.before === undefined ? ask<T>(provider, method, params) : relay.before().then(() => ask<T>(provider, method, params));
    return relay === undefined ? pending : withDeadline(pending, relay.deadlineMs, walletConnectTimeout);
  };
  if (PROMPTS.has(method)) {
    if (lane.prompt !== null) return Promise.reject(new WalletError(REQUEST_PENDING, PENDING_MESSAGE));
    const pending = call();
    lane.prompt = pending;
    forget(pending, () => {
      if (lane.prompt === pending) lane.prompt = null;
    });
    return pending;
  }
  const key = `${method}:${JSON.stringify(params ?? [])}`;
  const shared = lane.reads.get(key);
  if (shared !== undefined) return shared as Promise<T>;
  const open = lane.prompt;
  const settled = () => undefined;
  const pending = open === null ? call() : open.then(settled, settled).then(call);
  lane.reads.set(key, pending);
  forget(pending, () => {
    if (lane.reads.get(key) === pending) lane.reads.delete(key);
  });
  return pending;
}

/**
 * Methods that wait on a person tapping a wallet app. The rest are
 * machine-speed, and a 20 s silence means the relay is gone, not that someone
 * is reading.
 */
const WC_USER_ACTION_METHODS = new Set(["personal_sign", "eth_sendTransaction", "wallet_switchEthereumChain"]);
const WC_USER_ACTION_TIMEOUT_MS = timing.wallet.userActionTimeoutMs;
const WC_READ_TIMEOUT_MS = timing.wallet.readTimeoutMs;

function walletConnectTimeout(): WalletError {
  return new WalletError(WALLETCONNECT_TIMEOUT, "Open your wallet app to continue, then try again.");
}

/**
 * The session sits on a chain the Maker portal does not serve and holds no
 * account on any it does. Reportable on purpose: the pairing proposed only
 * these networks, so this is a wallet or library anomaly worth seeing.
 */
function wrongChainError(): WalletError {
  return new WalletError(WALLETCONNECT_UNAVAILABLE, "Your wallet app is on another network and this connection cannot switch back. Use another wallet, or connect WalletConnect again.");
}

/** The session holds no account on `chainId` (the wallet app approved other networks only). */
function notOnThisConnection(chainId: number): WalletError {
  const name = chainLabel(chainId);
  return new WalletError(
    WALLETCONNECT_RECONNECT,
    `Your WalletConnect connection does not include ${name}. Use another wallet, or connect WalletConnect again and approve ${name} in your wallet app.`,
  );
}

function reconnectForTransactions(): WalletError {
  return new WalletError(WALLETCONNECT_RECONNECT, "Your WalletConnect connection does not allow transactions. Use another wallet, or connect WalletConnect again.");
}

/**
 * A request to the picked wallet. Over WalletConnect nothing is on screen (the
 * request goes to a phone that may be in a pocket), so it gets a deadline, and
 * it is checked for its network first: the provider relays each request for
 * its OWN current chain, which the wallet app can move. `chainId` names the
 * network a transaction must go out on; without it any of the Maker portal's
 * networks will do (a signature names its own). Not for the network switch
 * itself: that IS the move.
 */
function rpc<T>(method: string, params?: readonly unknown[], chainId?: number): Promise<T> {
  let provider: Eip1193Provider;
  try {
    provider = target();
  } catch (err) {
    return Promise.reject(err);
  }
  if (provider !== getWalletConnectProvider()) return send<T>(provider, method, params);
  const wc = provider as WalletConnectProvider;
  const deadlineMs = WC_USER_ACTION_METHODS.has(method) ? WC_USER_ACTION_TIMEOUT_MS : WC_READ_TIMEOUT_MS;
  if (method === "wallet_switchEthereumChain") return send<T>(wc, method, params, { deadlineMs });
  return send<T>(wc, method, params, {
    deadlineMs,
    before: async () => {
      if (await ensureWalletConnectChain(wc, chainId)) return;
      throw chainId === undefined ? wrongChainError() : notOnThisConnection(chainId);
    },
  });
}

function firstAccount(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const first: unknown = value[0];
  return typeof first === "string" && /^0x[0-9a-fA-F]{40}$/.test(first) ? first.toLowerCase() : null;
}

// ---- connecting ------------------------------------------------------------------------

/** The WalletConnect pairing in flight, if any: a second one is refused, Cancel releases it. */
let walletConnectInFlight: Promise<string> | null = null;

/**
 * Asks a wallet to connect and returns the account, lowercase: `rdns` when
 * given, else the picked wallet. An extension gets `eth_requestAccounts`,
 * straight from the click and nothing awaited first: it opens its popup only
 * while the user gesture is on the stack. WalletConnect pairs instead
 * (`connectWalletConnect`): its QR sheet is in the page, so it may load first.
 */
export function connect(rdns?: string): Promise<string> {
  const wanted = rdns ?? getSelectedRdns();
  if (wanted === WALLETCONNECT_RDNS) {
    if (walletConnectInFlight !== null) return Promise.reject(new WalletError(REQUEST_PENDING, PENDING_MESSAGE));
    const pending = connectWalletConnect(rdns === WALLETCONNECT_RDNS);
    walletConnectInFlight = pending;
    // Cleared only while it is still THIS attempt: Cancel releases it early,
    // and a cancelled attempt settling later must not clear the next one's.
    forget(pending, () => {
      if (walletConnectInFlight === pending) walletConnectInFlight = null;
    });
    return pending;
  }
  let provider: Eip1193Provider;
  try {
    provider = target(rdns);
  } catch (err) {
    return Promise.reject(err);
  }
  return send<unknown>(provider, "eth_requestAccounts").then((accounts) => {
    const account = firstAccount(accounts);
    if (account === null) throw new WalletError(WALLET_LOCKED, "The wallet returned no account");
    return account;
  });
}

/**
 * `enable()` opens the QR sheet and pairs, with no deadline (the sheet has its
 * own close button, and unlocking a phone takes a moment). `explicit` is true when the operator just
 * picked WalletConnect, false when only the remembered pick led here.
 */
async function connectWalletConnect(explicit: boolean): Promise<string> {
  let wc: WalletConnectProvider;
  let accounts: string[];
  try {
    // A provider dropped by an earlier Cancel may have paired after all, and
    // its hang-up may still be on the relay. Until its delete lands its
    // session sits in the storage every provider shares, and the init below
    // would rehydrate it. Awaited only when there is something to wait for,
    // and a teardown during the wait is caught by comparing generations.
    const orphansBeforeInit = orphanHangUpsSettled();
    if (orphansBeforeInit !== null) {
      const teardowns = walletConnectGeneration();
      await orphansBeforeInit;
      if (walletConnectGeneration() !== teardowns) throw new WalletConnectUnavailable("WalletConnect session was closed", "closed");
    }
    wc = await initWalletConnect();
    // A teardown that could not reach the relay leaves the pairing alive in
    // the library's own store, and `init()` rehydrates it with no prompt: then
    // `enable()` would return the PREVIOUS person's account without any QR
    // code. Hang up first; refuse when that fails too.
    if (walletConnectTeardownUnclean() && hasWalletConnectSession(wc)) {
      await wc.disconnect().catch(() => undefined);
      if (hasWalletConnectSession(wc)) {
        throw new WalletError(WALLETCONNECT_UNAVAILABLE, "The previous WalletConnect connection could not be ended. Check your connection and try again.");
      }
    }
    // Again, for an orphan that paired while the init above ran: its hang-up
    // expires every pending pairing, this attempt's QR code included.
    const orphansBeforeEnable = orphanHangUpsSettled();
    if (orphansBeforeEnable !== null) await orphansBeforeEnable;
    // Cancelled (or another wallet picked) while the init or a hang-up above
    // ran: `enable()` would now open a QR sheet nothing can close.
    if (getWalletConnectProvider() !== wc) throw new ConnectionCancelled();
    try {
      accounts = await enableWalletConnect(wc);
    } catch (err) {
      // Classified below; this only makes sure the attempt given up on cannot
      // come back as a silent sign-in (the sheet's X does not stop the pairing).
      dropUnpairedAttempt(wc);
      throw err;
    }
  } catch (err) {
    if (err instanceof ConnectionCancelled) throw err;
    if (err instanceof WalletConnectUnavailable) {
      // A Cancel or another pick overtook the init: never reported.
      if (err.reason === "closed") throw new ConnectionCancelled();
      // A remembered pick on a deployment that no longer offers WalletConnect:
      // forget it so the screens show the list; an explicit pick keeps it and
      // is owed the reason.
      if (!explicit) selectProvider(null);
      throw new WalletError(WALLETCONNECT_UNAVAILABLE, err.message);
    }
    // The QR sheet closed (its X, or Cancel): no wallet was involved.
    if (isWalletConnectReset(err)) throw new ConnectionCancelled();
    // The wallet app declined the proposal (4001, or WalletConnect's 5000).
    if (isUserRejected(err) || isWalletConnectRejection(err)) throw new WalletError(USER_REJECTED, "You refused the connection in your wallet app.");
    if (isWalletConnectExpiry(err)) throw new WalletError(WALLETCONNECT_EXPIRED, "The connection request expired before it was approved. Try again.");
    // A dead relay, a blocked origin, a project the dashboard has not
    // propagated: reportable, the failure this integration is most likely to hit.
    throw toWalletError(err);
  }
  if (getWalletConnectProvider() !== wc) {
    // Torn down while pairing, and the phone approved anyway: hang up rather
    // than re-attach a session the singleton has already forgotten.
    void hangUpOrphan(wc);
    throw new ConnectionCancelled();
  }
  // This pairing is ours and was established just now.
  clearWalletConnectUnclean();
  const home = walletConnectHome(wc) ?? walletConnectNetworks(wc)?.[0] ?? null;
  if (home !== null && !sessionGrantsSigning(wc, home)) {
    // The wallet app approved a subset of what was asked: without the
    // signature nothing can sign in, so hang up rather than fail later.
    await disconnectWalletConnect();
    walletConnectChanged();
    throw new WalletError(WALLETCONNECT_UNAVAILABLE, "Your wallet app did not allow the sign-in signature. Use another wallet.");
  }
  const account = firstAccount(accounts);
  if (account === null) throw new WalletError(WALLET_LOCKED, "The wallet returned no account");
  listenForEnd();
  walletConnectChanged();
  return account;
}

/**
 * A failed `enable()` that left the provider with no session: drop it from the
 * singleton, as Cancel does, so the teardown arms the late-session watcher.
 * The sheet's X rejects `enable()` but does not stop the pairing (the
 * library's abort is a no-op in 2.25.0): a phone that approves afterwards
 * would give THIS provider a session, and the next click's `enable()` would
 * sign in with no QR code. A provider that HAS a session is left alone.
 */
function dropUnpairedAttempt(wc: WalletConnectProvider): void {
  if (getWalletConnectProvider() !== wc || hasWalletConnectSession(wc)) return;
  tearDownWalletConnect();
}

/**
 * Cancel on the sign-in card, while WalletConnect loads or pairs: closes the
 * QR sheet (the only thing that unwinds `enable()`), drops the provider (its
 * teardown hangs up on a late session) and releases the pairing guard at once,
 * so every wallet can be picked again. The pick survives: cancelling a scan is
 * not choosing another wallet.
 */
export function cancelConnect(): void {
  // Order matters: the sheet belongs to the provider the singleton still holds.
  closeWalletConnectModal();
  tearDownWalletConnect();
  walletConnectInFlight = null;
}

/**
 * Ends the wallet session this page holds: a WalletConnect pairing is hung
 * up, so the phone stops showing a live session for this browser, and a
 * WalletConnect pick is forgotten (a pairing the relay never heard end would
 * otherwise be resumed at the next boot, on a shared machine the previous
 * person's). An extension has no session to end, and its pick is kept.
 * Sign-out, and "Use another wallet" while WalletConnect is the wallet.
 */
export function disconnect(): void {
  if (getSelectedRdns() === WALLETCONNECT_RDNS) selectProvider(null);
  tearDownWalletConnect();
}

/** Outcome of `restoreWalletSession()`; see main.tsx. */
export type WalletRestore = "none" | "restored" | "stale" | "pending";

/** A hung relay must not hold the boot screen open indefinitely. */
const RESTORE_TIMEOUT_MS = timing.wallet.restoreTimeoutMs;

/**
 * Boot hook (main.tsx), awaited before the router mounts: re-attaches a
 * WalletConnect session this browser still holds, never prompting (the
 * library rehydrates it inside `init()`; `enable()` is not called). Only a
 * browser that picked WalletConnect pays the dynamic import; everyone else
 * resolves "none" at once.
 *
 *   - "restored": the session is live; the pick is used as is.
 *   - "stale": picked, and no usable session (ended on the phone, expired, a
 *     teardown the relay never heard, or no project id any more): dropped, so
 *     the screens show the list with WalletConnect as the last used.
 *   - "pending": the deadline fired while the package or the relay was still
 *     loading. NOT stale: tearing down here would delete a healthy pairing on
 *     every slow cold load. The provider is adopted when it lands.
 *
 * The Maker portal session is not the connected account, so nothing here
 * signs anyone out.
 */
export async function restoreWalletSession(): Promise<WalletRestore> {
  if (getSelectedRdns() !== WALLETCONNECT_RDNS) return "none";
  let wc: WalletConnectProvider | null = null;
  let timedOut = false;
  try {
    wc = await withDeadline(initWalletConnect(), RESTORE_TIMEOUT_MS, () => {
      timedOut = true;
      return walletConnectTimeout();
    });
  } catch {
    // No project id, a dead relay, a cleared session: handled below.
    wc = null;
  }
  if (wc !== null && hasWalletConnectSession(wc) && !walletConnectTeardownUnclean()) {
    listenForEnd();
    walletConnectChanged();
    return "restored";
  }
  if (timedOut) {
    void initWalletConnect().then(
      (late) => {
        if (!hasWalletConnectSession(late)) return;
        // A connect started meanwhile awaits this same provider and owns the outcome.
        if (walletConnectInFlight !== null) return;
        // The pairing a failed teardown meant to end: end it, never adopt it.
        if (walletConnectTeardownUnclean()) {
          tearDownWalletConnect();
          return;
        }
        listenForEnd();
        walletConnectChanged();
      },
      () => undefined,
    );
    return "pending";
  }
  // Bumps the generation, so a provider still being built is hung up on, not adopted.
  await disconnectWalletConnect();
  walletConnectChanged();
  return "stale";
}

// ---- accounts and network --------------------------------------------------------

/**
 * The accounts the picked wallet lets this site see (`eth_accounts`): never
 * prompts, never rejects. Empty with no pick, a locked wallet or a failure.
 */
export async function getAccounts(): Promise<string[]> {
  if (selectedWallet() === null) return [];
  try {
    const value = await rpc<unknown>("eth_accounts");
    return Array.isArray(value) ? value.filter((a): a is string => typeof a === "string").map((a) => a.toLowerCase()) : [];
  } catch {
    return [];
  }
}

/** What a wallet answers `wallet_requestPermissions` when it has no account list to show: the plain connect is asked instead. */
const NO_ACCOUNT_LIST = new Set<unknown>([-32601, 4200, -32602, "-32601", "4200", "-32602"]);

function lowercaseAccounts(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((a): a is string => typeof a === "string" && /^0x[0-9a-fA-F]{40}$/.test(a)).map((a) => a.toLowerCase()) : [];
}

/** Whether the picked wallet can show its account list here: an extension can; a WalletConnect session cannot. */
export function canChooseAccount(): boolean {
  return getSelectedRdns() !== WALLETCONNECT_RDNS && selectedWallet() !== null;
}

/**
 * The picked wallet's account list (EIP-2255, `wallet_requestPermissions`
 * for `eth_accounts`), then the accounts this site may use, lowercase, in the
 * wallet's order. A wallet without the method is asked to connect instead.
 * Not async before the request: call it straight from the click. A closed
 * list is a refusal (4001). Nothing is signed or sent, and the session stays.
 */
export function requestAccountChoice(): Promise<string[]> {
  if (!canChooseAccount()) return Promise.reject(new WalletError(NO_ACCOUNT_CHOOSER_CODE, "This wallet cannot show its accounts here"));
  let provider: Eip1193Provider;
  try {
    provider = target();
  } catch (err) {
    return Promise.reject(err);
  }
  return send<unknown>(provider, "wallet_requestPermissions", [{ eth_accounts: {} }]).then(
    () => send<unknown>(provider, "eth_accounts").then(lowercaseAccounts),
    (err: unknown) => {
      if (!codesOf(err).some((c) => NO_ACCOUNT_LIST.has(c))) throw err;
      return send<unknown>(provider, "eth_requestAccounts").then(lowercaseAccounts);
    },
  );
}

export async function getChainId(): Promise<number> {
  const hex = await rpc<unknown>("eth_chainId");
  const id = typeof hex === "string" ? Number.parseInt(hex, 16) : typeof hex === "number" ? hex : Number.NaN;
  if (!Number.isFinite(id)) throw new WalletError("BAD_CHAIN", "The wallet reported no network");
  return id;
}

/**
 * `wallet_switchEthereumChain`; a refusal (4001) or an unknown network (4902)
 * rejects. Over WalletConnect a network the connection holds no account on is
 * refused before anything is relayed (the approved set is frozen at pairing,
 * so the wallet app could not switch the session there), and a switch between
 * two approved networks never leaves the browser.
 */
export async function switchToChain(chainId: number): Promise<void> {
  const wc = pickedWalletConnect();
  if (wc !== null) {
    const offered = walletConnectNetworks(wc);
    if ((offered !== null && !offered.includes(chainId)) || walletConnectApproves(wc, chainId) === false) throw notOnThisConnection(chainId);
  }
  try {
    await rpc<unknown>("wallet_switchEthereumChain", [{ chainId: toChainIdHex(chainId) }]);
  } catch (err) {
    if (wc === null) throw err;
    const e = toWalletError(err);
    // Outcomes the screens already word keep their codes over the relay: a
    // phone tapping "Reject" is a real 4001.
    if (isUserRejected(e) || isRequestPending(e) || e.code === WALLETCONNECT_TIMEOUT) throw e;
    throw new WalletError(WALLETCONNECT_UNAVAILABLE, `Switch your wallet app to ${chainLabel(chainId)}, then try again.`);
  }
  if (wc !== null) walletConnectHoldOn(wc, chainId);
}

/** `personal_sign` of a UTF-8 message, hex-encoded as EIP-1193 wallets (and WalletConnect's) expect. */
export async function signMessage(address: string, message: string): Promise<string> {
  const signature = await rpc<unknown>("personal_sign", [toHex(message), address]);
  if (typeof signature !== "string" || !signature.startsWith("0x")) throw new WalletError("BAD_SIGNATURE", "The wallet returned no signature");
  return signature;
}

/**
 * Sends exactly `to` and `data`, with no value, from `from`. Resolves with the
 * transaction hash. `chainId` is the network the call was prepared for: over
 * WalletConnect the transaction is relayed for that network only (moved there
 * locally first), and refused when the connection holds no account there or
 * grants no transaction. An extension must already be on that network: the
 * caller switches it.
 */
export async function sendTransaction(tx: { from: string; to: string; data: string; chainId?: number | undefined }): Promise<string> {
  const wc = pickedWalletConnect();
  const params: Record<string, string> = { from: tx.from, to: tx.to, data: tx.data, value: "0x0" };
  if (wc !== null && tx.chainId !== undefined) {
    const offered = walletConnectNetworks(wc);
    if ((offered !== null && !offered.includes(tx.chainId)) || walletConnectApproves(wc, tx.chainId) === false) throw notOnThisConnection(tx.chainId);
    if (!sessionGrantsMethods(wc, tx.chainId, ["eth_sendTransaction"])) throw reconnectForTransactions();
    params.chainId = toChainIdHex(tx.chainId);
  }
  let hash: unknown;
  try {
    hash = await rpc<unknown>("eth_sendTransaction", [params], wc === null ? undefined : tx.chainId);
  } catch (err) {
    if (wc !== null && isWalletConnectMethodRefusal(err)) throw reconnectForTransactions();
    throw err;
  }
  if (typeof hash !== "string" || !/^0x[0-9a-fA-F]{64}$/.test(hash)) throw new WalletError("BAD_HASH", "The wallet returned no transaction hash");
  return hash;
}

/**
 * A contract read (`eth_call` at the latest block) through the picked wallet:
 * an extension answers for the network it is on, so read only once it is on
 * `chainId`; over WalletConnect the read is made for `chainId`. Resolves with
 * the returned data.
 */
export async function readContract(call: { to: string; data: string; chainId: number }): Promise<string> {
  const out = await rpc<unknown>("eth_call", [{ to: call.to, data: call.data }, "latest"], call.chainId);
  if (typeof out !== "string" || !/^0x[0-9a-fA-F]*$/.test(out)) throw new WalletError("BAD_READ", "The wallet returned an unreadable answer");
  return out;
}

/** A sent transaction's outcome, read through the picked wallet as `readContract` is: null while it is not mined; `unknown` when the receipt names no status. */
export async function getTransactionStatus(hash: string, chainId: number): Promise<"success" | "reverted" | "unknown" | null> {
  const receipt = await rpc<unknown>("eth_getTransactionReceipt", [hash], chainId);
  if (receipt === null || receipt === undefined) return null;
  const status = (receipt as { status?: unknown }).status;
  if (status === "0x1" || status === 1) return "success";
  if (status === "0x0" || status === 0) return "reverted";
  return "unknown";
}

/** Subscribes to the picked wallet's event; nothing when none is usable. Re-subscribe when the pick changes. */
function subscribe(event: string, listener: (...args: never[]) => void): Unsubscribe {
  const provider = selectedWallet()?.provider;
  if (provider?.on === undefined) return () => undefined;
  provider.on(event, listener);
  return () => provider.removeListener?.(event, listener);
}

/** The picked wallet switched (or dropped) accounts: lowercase, first first. */
export function onAccountsChanged(handler: (accounts: string[]) => void): Unsubscribe {
  return subscribe("accountsChanged", ((accounts: unknown) => {
    handler(Array.isArray(accounts) ? accounts.filter((a): a is string => typeof a === "string").map((a) => a.toLowerCase()) : []);
  }) as (...args: never[]) => void);
}

/** The picked wallet moved to another network. */
export function onChainChanged(handler: (chainId: number) => void): Unsubscribe {
  return subscribe("chainChanged", ((value: unknown) => {
    const id = typeof value === "string" ? Number.parseInt(value, 16) : typeof value === "number" ? value : Number.NaN;
    if (Number.isFinite(id)) handler(id);
  }) as (...args: never[]) => void);
}
